import { defineStore } from 'pinia';
import { parseFile, detectKind, detectDelimiter, detectMultiSeparator } from '@vft/core/parser';
import { detectEncoding } from '@vft/core/parser/detectEncoding';
import { setMultiSeparator as applyMultiSeparator } from '@vft/core/validation';

// Mostra de bytes que s'inspecciona per detectar la codificació del CSV. N'hi ha
// prou amb el principi del fitxer (els accents apareixen a noms/adreces de seguida)
// i evita llegir-lo sencer en fitxers grans.
const ENCODING_SAMPLE_BYTES = 512 * 1024;

export const useFileStore = defineStore('file', {
  state: () => ({
    file: null,
    fileName: '',
    kind: null,
    encoding: 'utf-8',
    // true = la codificació s'ha autodetectat amb confiança alta (es mostra el
    // distintiu «detectada»). Una tria manual de l'usuari el posa a false.
    encodingDetected: false,
    headers: [],
    rows: [],
    delimiter: null,
    // tria de l'usuari per al separador: '' = Auto (autodetecció de PapaParse). Un valor
    // concret (';', ',', '\t', '|') força aquest separador en (re)parsejar.
    delimiterChoice: '',
    // separador de multiresposta (DINS d'una cel·la, p.ex. '|' a "a|b"): '' = Auto (ordre de
    // candidats / el del camp); un valor el força a la validació/transformació (splitMulti).
    multiSeparator: '',
    // separador de multiresposta AUTODETECTAT de la mostra (per mostrar-lo com a badge quan
    // multiSeparator és Auto). En Auto, és també el que s'usa (override efectiu).
    multiSeparatorDetected: null,
    parsing: false,
    error: null,
  }),
  getters: {
    hasData: (s) => s.headers.length > 0 && s.rows.length > 0,
    // Cert quan és un CSV i la codificació no s'ha pogut determinar: la UI obliga
    // a triar-la manualment abans de deixar continuar.
    needsEncoding: (s) => s.kind === 'csv' && !s.encoding,
  },
  actions: {
    async setFile(file) {
      this.reset();
      this.file = file;
      this.fileName = file?.name || '';
      this.kind = detectKind(this.fileName);
      if (this.kind === 'unknown') {
        this.error = `Format no suportat: ${this.fileName}`;
        return;
      }
      if (this.kind === 'csv') {
        await this.detectCsvEncoding(file);
        await this.detectCsvDelimiter(file);
        await this.detectCsvMultiSeparator(file);
      }
      // XLSX: s'analitza sempre el primer full (el parser hi cau per defecte).
    },
    // Llegeix una mostra del principi del fitxer i n'autodetecta la codificació.
    // Amb confiança alta l'autoselecciona; si és ambigua (UTF-16, codepage
    // desconegut…) la deixa buida perquè l'usuari l'hagi de triar.
    async detectCsvEncoding(file) {
      try {
        const slice = file.slice(0, ENCODING_SAMPLE_BYTES);
        const buffer = await slice.arrayBuffer();
        const det = detectEncoding(buffer);
        if (det.confidence === 'high' && det.encoding) {
          this.encoding = det.encoding;
          this.encodingDetected = true;
        } else {
          this.encoding = '';
          this.encodingDetected = false;
        }
      } catch {
        // Si no es pot llegir la mostra, que l'usuari triï manualment.
        this.encoding = '';
        this.encodingDetected = false;
      }
    },
    setEncoding(enc) {
      this.encoding = enc;
      this.encodingDetected = false; // tria manual de l'usuari
    },
    // Autodetecta el separador d'una mostra del fitxer (guess de PapaParse) per mostrar-lo
    // a la pantalla de càrrega. Best-effort: si falla, es deixa buit i el parseig complet
    // el tornarà a provar.
    async detectCsvDelimiter(file) {
      try {
        const slice = file.slice(0, ENCODING_SAMPLE_BYTES);
        this.delimiter = (await detectDelimiter(slice, { encoding: this.encoding || 'utf-8' })) || null;
      } catch {
        this.delimiter = null;
      }
    },
    setDelimiter(delim) {
      this.delimiterChoice = delim; // '' = Auto; un valor força el separador
    },
    // Autodetecta el separador de multiresposta d'una mostra (best-effort) i fixa l'override
    // efectiu: la tria de l'usuari si n'hi ha, si no el detectat (Auto usa el detectat).
    async detectCsvMultiSeparator(file) {
      try {
        const slice = file.slice(0, ENCODING_SAMPLE_BYTES);
        this.multiSeparatorDetected = (await detectMultiSeparator(slice, {
          encoding: this.encoding || 'utf-8',
          delimiter: this.delimiterChoice || this.delimiter || '',
        })) || null;
      } catch {
        this.multiSeparatorDetected = null;
      }
      applyMultiSeparator(this.multiSeparator || this.multiSeparatorDetected || '');
    },
    setMultiSeparator(sep) {
      this.multiSeparator = sep;  // '' = Auto (usa el detectat); un valor força el separador
      applyMultiSeparator(sep || this.multiSeparatorDetected || '');
    },
    async parse(opts = {}) {
      if (!this.file || !this.kind || this.kind === 'unknown') return;
      this.parsing = true;
      this.error = null;
      try {
        const result = await parseFile(this.file, {
          kind: this.kind,
          encoding: this.encoding,
          delimiter: this.delimiterChoice,
        }, opts.onProgress);
        this.headers = result.headers;
        this.rows = result.rows;
        this.delimiter = result.delimiter || null;
      } catch (err) {
        this.error = err?.message || 'Error analitzant el fitxer';
        throw err;
      } finally {
        this.parsing = false;
      }
    },
    reset() {
      this.file = null;
      this.fileName = '';
      this.kind = null;
      this.encoding = 'utf-8';
      this.encodingDetected = false;
      this.headers = [];
      this.rows = [];
      this.delimiter = null;
      this.delimiterChoice = '';
      this.multiSeparator = '';
      this.multiSeparatorDetected = null;
      applyMultiSeparator('');
      this.parsing = false;
      this.error = null;
    },
  },
});
